import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Caravan, Loader2, AlertCircle, Clock } from 'lucide-react';
import { useAuthStore, homePathFor } from '../store/useAuthStore';
import { BRAND_NAME } from '../lib/brand';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const clearError = useAuthStore((s) => s.clearError);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => clearError, [clearError]);

  // Shown once, then cleared, so it does not persist across later visits.
  const expired = location.state?.expired;
  const clearExpired = useAuthStore((s) => s.clearExpired);
  useEffect(() => { if (expired) clearExpired(); }, [expired, clearExpired]);

  // Already signed in — don't show a login form.
  if (status === 'signed_in' && user) {
    return <Navigate to={location.state?.from ?? homePathFor(user.role)} replace />;
  }

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const result = await login(email, password);
    setSubmitting(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }
    const role = useAuthStore.getState().user?.role;
    navigate(location.state?.from ?? homePathFor(role), { replace: true });
  };

  return (
    <div className="grid min-h-full place-items-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="mb-3 grid h-12 w-12 place-items-center rounded-xl bg-brand-600 text-white">
            <Caravan className="h-6 w-6" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Sign in to {BRAND_NAME}</h1>
          <p className="mt-1 text-sm text-slate-500">Tenants, staff, and administrators.</p>
        </div>

        {expired && (
          <p role="status" className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs font-medium text-amber-900">
            <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Your session ended, so we signed you out. Sign in again and we&apos;ll take you
            back to where you were.
          </p>
        )}

        <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
          {error && (
            <p role="alert" className="mb-4 flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-xs font-medium text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {error}
            </p>
          )}

          <label className="field-label mb-1" htmlFor="email">Email</label>
          <input
            id="email" type="email" autoComplete="email" required autoFocus
            className="field-input mb-4" value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <div className="mb-1 flex items-baseline justify-between">
            <label className="field-label" htmlFor="password">Password</label>
            <Link to="/forgot-password" className="text-xs font-semibold text-brand-700 hover:underline">
              Forgot?
            </Link>
          </div>
          <input
            id="password" type="password" autoComplete="current-password" required
            className="field-input mb-5" value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button type="submit" disabled={submitting} className="btn-primary w-full py-2.5">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-slate-600">
          Storing a vehicle with us?{' '}
          <Link to="/register" className="font-semibold text-brand-700 hover:underline">Create an account</Link>
        </p>

        {import.meta.env.DEV && <DevAccounts onPick={(e) => { setEmail(e); setPassword('ParktleDev!2026'); }} />}
      </div>
    </div>
  );
}

/** Development-only shortcut so each role can be exercised without a password manager. */
function DevAccounts({ onPick }) {
  const accounts = [
    ['owner@parktle.com', 'Super admin'],
    ['admin@parktle.com', 'Admin'],
    ['gate@parktle.com', 'Attendant'],
    ['ellen.park@example.com', 'Customer'],
  ];
  return (
    <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white/60 p-3">
      <p className="mb-2 text-[12px] font-bold uppercase tracking-wide text-slate-400">Dev accounts</p>
      <div className="flex flex-wrap gap-1.5">
        {accounts.map(([addr, label]) => (
          <button key={addr} type="button" onClick={() => onPick(addr)} className="chip text-[12px]">
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
